import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { calculateConsumption, extrapolateToYear } from '../utils/energyLogic';
import { COLORS } from '../utils/constants';

const { FiCheckSquare, FiSquare, FiEdit2, FiTrash2, FiCopy, FiBarChart2, FiChevronDown, FiChevronUp } = FiIcons;

const formatNumber = (value, type) => {
  if (value === null || value === undefined) return '—';
  
  if (type === 'electricity') {
    // Strom: 1 Nachkommastelle
    return value.toLocaleString('de-DE', { 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    });
  } else if (type === 'gas' || type === 'water' || type === 'waterCold' || type === 'waterWarm') {
    // Gas/Wasser: 3 Nachkommastellen
    return value.toLocaleString('de-DE', { 
      minimumFractionDigits: 3, 
      maximumFractionDigits: 3 
    });
  }
  
  return value.toLocaleString('de-DE', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

const ReadingsList = ({ meter, objectId, onAnalyze, onUpdateReading, onDeleteReading, onDuplicateReading }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAllReadings, setShowAllReadings] = useState(false);

  if (!meter.readings || meter.readings.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 text-center text-gray-500">
        <p>Noch keine Ablesungen vorhanden.</p>
        <p className="text-sm mt-2">Erfassen Sie Ihren ersten Zählerstand oben.</p>
      </div>
    );
  }

  const sortedReadings = [...meter.readings].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  // Wenn genau 2 ausgewählt sind, zeige diese beiden, sonst letzte 2
  let visibleReadings;
  if (selectedIds.length === 2 && !showAllReadings) {
    visibleReadings = sortedReadings.filter(r => selectedIds.includes(r.id));
  } else if (showAllReadings) {
    visibleReadings = sortedReadings;
  } else {
    visibleReadings = sortedReadings.slice(0, 2);
  }

  const hiddenCount = sortedReadings.length - 2;

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else if (prev.length < 2) {
        return [...prev, id];
      } else {
        return [prev[1], id];
      }
    });
  };

  const handleAnalyze = () => {
    if (selectedIds.length === 2) {
      const selected = sortedReadings.filter(r => selectedIds.includes(r.id));
      onAnalyze(selected.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    } else {
      // Fallback: letzte 2
      const lastTwo = sortedReadings.slice(0, 2).reverse();
      onAnalyze(lastTwo);
    }
  };

  const handleDelete = (readingId) => {
    if (window.confirm('Möchten Sie diesen Eintrag wirklich unwiderruflich löschen?')) {
      onDeleteReading(objectId, meter.id, readingId);
      setSelectedIds(prev => prev.filter(id => id !== readingId));
    }
  };

  const handleDuplicate = (reading) => {
    if (onDuplicateReading) {
      onDuplicateReading(objectId, meter.id, reading.id);
    }
  };

  const renderReading = (reading, index) => {
    const isSelected = selectedIds.includes(reading.id);
    const prevReading = sortedReadings[index + 1];
    
    let consumption = null;
    let forecast = null;
    let errorMessage = null;
    
    if (prevReading) {
      const timeDiff = new Date(reading.timestamp) - new Date(prevReading.timestamp);
      const hours = timeDiff / (1000 * 60 * 60);
      const days = hours / 24;
      
      if (days <= 0) {
        errorMessage = 'Zeitraum ungültig (0 Tage oder negativ)';
      } else {
        consumption = calculateConsumption(prevReading, reading, meter);
        if (consumption !== null) {
          forecast = extrapolateToYear(consumption, days);
        }
      }
    }

    return (
      <motion.div
        key={reading.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
          isSelected 
            ? 'border-orange-500 bg-orange-50 shadow-md' 
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => toggleSelection(reading.id)}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 text-xl" style={{ color: isSelected ? COLORS.secondary : '#9ca3af' }}>
            <SafeIcon icon={isSelected ? FiCheckSquare : FiSquare} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-500">
                {format(new Date(reading.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(reading);
                  }}
                  className="text-blue-500 hover:text-blue-700 transition-colors"
                  title="Duplizieren"
                >
                  <SafeIcon icon={FiCopy} size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(reading.id);
                  }}
                  className="text-red-500 hover:text-red-700 transition-colors"
                  title="Löschen"
                >
                  <SafeIcon icon={FiTrash2} size={16} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-gray-500">Zählerstand</div>
                <div className="font-bold" style={{ color: COLORS.primary }}>
                  {formatNumber(reading.value, meter.type)}
                </div>
                <div className="text-xs text-gray-400">
                  {meter.type === 'gas' || meter.type === 'water' || meter.type === 'waterCold' || meter.type === 'waterWarm' ? 'm³' : 'kWh'}
                </div>
              </div>

              {errorMessage ? (
                <div className="col-span-3 text-xs text-red-500 italic">
                  {errorMessage}
                </div>
              ) : consumption !== null ? (
                <>
                  <div>
                    <div className="text-xs text-gray-500">Verbrauch</div>
                    <div className="font-bold text-green-600">
                      {formatNumber(consumption, 'electricity')} kWh
                    </div>
                  </div>

                  {forecast !== null && forecast > 0 && (
                    <div className="col-span-2">
                      <div className="text-xs text-gray-500">365-Tage-Prognose</div>
                      <div className="font-bold" style={{ color: COLORS.secondary }}>
                        {formatNumber(forecast, 'electricity')} kWh/Jahr
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="col-span-3 text-xs text-gray-400 italic">
                  Erste Ablesung - Verbrauch wird ab der nächsten Ablesung berechnet
                </div>
              )}
            </div>

            {meter.type === 'gas' && reading.gasParams && (
              <div className="mt-2 p-2 rounded text-xs" style={{ backgroundColor: `${COLORS.gas}15` }}>
                <strong>Gas-Parameter:</strong> z-Zahl {reading.gasParams.zNumber.toFixed(4)} · Brennwert {reading.gasParams.heatingValue.toFixed(3)} kWh/m³
              </div>
            )}

            {reading.photo && (
              <div className="mt-3">
                <img 
                  src={reading.photo} 
                  alt="Zähler" 
                  className="w-full h-32 object-cover rounded border"
                />
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold" style={{ color: COLORS.primary }}>
          Ablesungen ({sortedReadings.length})
        </h3>
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAnalyze}
          className="px-4 py-2 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
          style={{ backgroundColor: COLORS.secondary }}
        >
          <SafeIcon icon={FiBarChart2} />
          <span>Zeitraum analysieren</span>
        </motion.button>
      </div>

      {selectedIds.length > 0 && selectedIds.length < 2 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Wählen Sie noch {2 - selectedIds.length} Eintrag aus für den Vergleich, oder klicken Sie auf "Zeitraum analysieren" um die letzten 2 zu nutzen.
        </div>
      )}

      <div className="space-y-3">
        <AnimatePresence>
          {visibleReadings.map((reading, index) => {
            const actualIndex = sortedReadings.findIndex(r => r.id === reading.id);
            return renderReading(reading, actualIndex);
          })}
        </AnimatePresence>
      </div>

      {hiddenCount > 0 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowAllReadings(!showAllReadings)}
          className="w-full mt-4 py-3 px-4 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-all flex items-center justify-center gap-2 font-medium"
        >
          <SafeIcon icon={showAllReadings ? FiChevronUp : FiChevronDown} />
          {showAllReadings ? 'Weniger anzeigen' : `${hiddenCount} weitere Ablesung${hiddenCount > 1 ? 'en' : ''} anzeigen`}
        </motion.button>
      )}
    </div>
  );
};

export default ReadingsList;